import { useEffect, useRef, useState } from 'react'
import { AlertOctagon, Loader2, RotateCcw, X } from 'lucide-react'
import { useFleet } from '../state/store'
import { CC, EYEBROW } from '../cc/tokens'
import { ago, clock } from '../lib/format'

function whenMoved(ts: number | null | undefined): string {
  if (ts == null) return '—'
  return new Date(ts).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}
import { BreakdownPhotos } from './BreakdownPhotos'
import { BreakdownHistory } from './BreakdownHistory'

/**
 * Mark a vehicle broken, or put it back on the road.
 *
 * The one piece of fleet state telemetry cannot supply. A truck with a snapped leaf spring
 * reports a healthy pack, a valid position and a speed of zero -- indistinguishable from
 * one queueing at a gate. Only a person can tell the difference, so this is the smallest
 * form that captures what a dispatcher does with that knowledge:
 *
 *   WHAT is wrong          category -- so it can be counted, not just read
 *   HOW BAD                severity -- decides whether the load needs re-planning now
 *   HOW LONG               eta      -- the only field a planner actually schedules against
 *   WHO TO CALL            mobile   -- prefilled from the driver roster, editable
 *
 * Everything else is the note. Resisting the urge to add a dozen structured fields is the
 * design: a form that takes two minutes gets filled in after the fact, from memory, or
 * not at all -- and a breakdown log nobody fills in is worse than none, because the fleet
 * report will quietly treat those hours as available.
 *
 * State lives in the serving layer, not here, and is broadcast over SSE -- so a second
 * console sees the truck go red without a refresh.
 */

// Exported so BreakdownsPanel's edit form offers the exact same options -- one list, not
// a second one that quietly drifts.
export const CATEGORIES = [
  'Tyre / wheel',
  'Brakes',
  'Battery / HV pack',
  'Motor / drivetrain',
  'Charger fault',
  'Suspension',
  'Electrical',
  'Accident',
  'Other',
]

export const SEVERITIES = [
  { id: 'minor', label: 'Minor', hint: 'can continue, fix at hub' },
  { id: 'major', label: 'Major', hint: 'must return, load transfers' },
  { id: 'immobilised', label: 'Immobilised', hint: 'stranded, recovery needed' },
] as const

export function BreakdownForm({
  vehicleId, phone, onClose,
}: { vehicleId: string; phone?: string | null; onClose: () => void }): JSX.Element {
  const existing = useFleet((s) => s.breakdowns.get(vehicleId))
  const [category, setCategory] = useState(existing?.category ?? CATEGORIES[0]!)
  const [severity, setSeverity] = useState<string>(existing?.severity ?? 'major')
  const [eta, setEta] = useState(existing?.eta_min != null ? String(existing.eta_min) : '')
  const [mobile, setMobile] = useState(existing?.mobile ?? phone ?? '')
  const [note, setNote] = useState(existing?.note ?? '')
  const [busy, setBusy] = useState<'save' | 'clear' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const first = useRef<HTMLButtonElement>(null)
  // Set the instant the operator changes any field, so the hydration catch-up below
  // knows better than to overwrite something already being typed.
  const touched = useRef(false)
  // /api/state can still be hydrating when this form mounts (it resolves after the
  // console is already interactive) -- if "Update breakdown" opens in that window,
  // `existing` is briefly undefined and the fields above seed blank even though a real
  // record exists. Re-seed once, the moment it lands, so the note isn't lost. Guarded to
  // fire only on that first undefined -> defined catch-up AND only while the operator
  // hasn't touched anything yet, so it never overwrites what they're already typing.
  const hydrated = useRef(existing)
  useEffect(() => {
    if (hydrated.current == null && existing != null && !touched.current) {
      setCategory(existing.category ?? CATEGORIES[0]!)
      setSeverity(existing.severity ?? 'major')
      setEta(existing.eta_min != null ? String(existing.eta_min) : '')
      setMobile(existing.mobile ?? phone ?? '')
      setNote(existing.note ?? '')
    }
    hydrated.current = existing
  }, [existing, phone])

  useEffect(() => { first.current?.focus() }, [])
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])

  async function submit(clear: boolean): Promise<void> {
    const mobileDigits = mobile.replace(/\D/g, '')
    if (!clear && mobile.trim() !== '' && mobileDigits.length !== 10) {
      setError('contact number must be 10 digits')
      return
    }
    setBusy(clear ? 'clear' : 'save')
    setError(null)
    try {
      const res = await fetch(`/api/vehicle/${encodeURIComponent(vehicleId)}/breakdown`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(clear ? { clear: true } : {
          category, severity, note: note.trim() || null,
          mobile: mobile.trim() || null,
          eta_min: eta.trim() === '' ? null : Number(eta),
        }),
      })
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
      // Applied directly, not left to the SSE broadcast alone: mark_breakdown's own
      // response body IS the exact entry it publishes over SSE (`return entry` after
      // `_publish({"type": "breakdown", "data": entry})` in serving/app.py), so this is
      // not a second source of truth, just the same one arriving without the extra hop.
      // That hop matters on a 2-pod deployment with no session affinity: _publish() only
      // reaches browsers whose SSE connection happens to be pinned to the SAME pod that
      // handled this POST, which is routinely not this one -- the operator who just
      // submitted the form would otherwise watch their own FLEET tab show the truck as
      // healthy for up to the 15s per-pod resync tick takes to catch up, confirmed as
      // the reported "need to hard refresh to see it" bug.
      useFleet.getState().applyEvent({ type: 'breakdown', data: await res.json() })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not reach the server')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-[rgba(15,23,42,0.28)] p-4"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div role="dialog" aria-modal="true" aria-label={`Breakdown for ${vehicleId}`}
           className="flex max-h-[90vh] w-full max-w-[420px] animate-slide-up flex-col rounded-xl
                      border border-[rgba(15,23,42,0.10)] bg-white
                      shadow-[0_24px_60px_-24px_rgba(15,23,42,0.55)]">
        <header className="flex items-center gap-2.5 border-b border-[rgba(15,23,42,0.07)] px-4 py-3">
          <span className="grid h-7 w-7 place-items-center rounded-lg bg-[rgba(244,63,94,0.10)]">
            <AlertOctagon size={15} className="text-[#9F1239]" />
          </span>
          <div className="min-w-0">
            <div className="font-mono text-[13px] font-semibold text-[#0F172A]">{vehicleId}</div>
            <div className={EYEBROW}>{existing ? 'update breakdown' : 'mark breakdown'}</div>
          </div>
          <button onClick={onClose} aria-label="Close"
                  className="ml-auto rounded p-1 text-[#8794A7] hover:bg-[rgba(15,23,42,0.05)]">
            <X size={15} />
          </button>
        </header>

        {existing && (
          <div className="flex items-center justify-between gap-2 border-b border-[rgba(15,23,42,0.07)]
                          bg-[rgba(15,23,42,0.02)] px-4 py-1.5 text-[11px] text-[#8794A7]">
            <span>reported {ago(existing.opened_ts ?? existing.ts)} ago · {clock(existing.opened_ts ?? existing.ts)}</span>
            {existing.resumed_moving_ts != null && (
              <span className="font-medium text-amber-700">
                started moving at {whenMoved(existing.resumed_moving_ts)} — resolve below
              </span>
            )}
          </div>
        )}

        <div className="grid min-h-0 flex-1 gap-3.5 overflow-y-auto px-4 py-4">
          <Field label="Fault">
            <select value={category} onChange={(e) => { touched.current = true; setCategory(e.target.value) }}
                    className="w-full rounded-lg border border-[rgba(15,23,42,0.12)] bg-white px-2.5 py-2
                               text-[13px] text-[#0F172A]">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>

          <Field label="Severity">
            <div className="grid grid-cols-3 gap-1.5">
              {SEVERITIES.map((s, i) => (
                <button
                  key={s.id}
                  ref={i === 0 ? first : undefined}
                  onClick={() => { touched.current = true; setSeverity(s.id) }}
                  title={s.hint}
                  className={`rounded-lg border px-2 py-2 text-left transition-colors ${
                    severity === s.id
                      ? 'border-[rgba(244,63,94,0.35)] bg-[rgba(244,63,94,0.10)]'
                      : 'border-[rgba(15,23,42,0.10)] bg-white hover:bg-[rgba(15,23,42,0.03)]'
                  }`}
                >
                  <span className={`block text-[12px] font-medium ${
                    severity === s.id ? 'text-[#9F1239]' : 'text-[#0F172A]'}`}>{s.label}</span>
                  <span className="mt-0.5 block text-[10px] leading-tight text-[#8794A7]">
                    {s.hint}
                  </span>
                </button>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Back in service (min)">
              <input value={eta}
                     onChange={(e) => { touched.current = true; setEta(e.target.value.replace(/[^\d]/g, '')) }}
                     inputMode="numeric" placeholder="unknown"
                     className="num w-full rounded-lg border border-[rgba(15,23,42,0.12)] bg-white
                                px-2.5 py-2 font-mono text-[13px] text-[#0F172A]" />
            </Field>
            <Field label="Contact">
              <input value={mobile}
                     onChange={(e) => {
                       touched.current = true
                       setMobile(e.target.value.replace(/[^\d+]/g, '').slice(0, 13))
                     }}
                     inputMode="tel" placeholder="driver mobile" maxLength={13}
                     className="num w-full rounded-lg border border-[rgba(15,23,42,0.12)] bg-white
                                px-2.5 py-2 font-mono text-[13px] text-[#0F172A]" />
            </Field>
          </div>

          <Field label="Note">
            <textarea value={note} onChange={(e) => { touched.current = true; setNote(e.target.value) }} rows={2}
                      placeholder="where it stopped, what was tried, who is attending"
                      className="w-full resize-none rounded-lg border border-[rgba(15,23,42,0.12)]
                                 bg-white px-2.5 py-2 text-[13px] text-[#0F172A]
                                 placeholder:text-[#B3BECE]" />
          </Field>

          {error && (
            <p className="rounded-lg border border-[rgba(244,63,94,0.26)] bg-[rgba(244,63,94,0.10)]
                          px-2.5 py-2 text-[12px] text-[#9F1239]">
              Not saved — {error}. The vehicle is unchanged.
            </p>
          )}

          {existing?.breakdown_id != null && (
            <div className="grid gap-1.5 border-t border-[rgba(15,23,42,0.07)] pt-3">
              <span className={EYEBROW}>Photos</span>
              <BreakdownPhotos vehicleId={vehicleId} breakdownId={existing.breakdown_id} />
            </div>
          )}

          <BreakdownHistory vehicleId={vehicleId} />
        </div>

        <footer className="flex items-center gap-2 border-t border-[rgba(15,23,42,0.07)] px-4 py-3">
          {existing && (
            <button onClick={() => submit(true)} disabled={busy !== null}
                    className="flex items-center gap-1.5 rounded-lg border border-[rgba(15,23,42,0.12)]
                               px-2.5 py-2 text-[12px] text-[#047857] hover:bg-[rgba(16,185,129,0.08)]
                               disabled:opacity-50">
              {busy === 'clear' ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
              Back in service
            </button>
          )}
          <span className="flex-1" />
          <button onClick={onClose}
                  className="rounded-lg px-3 py-2 text-[12px] text-[#475569] hover:bg-[rgba(15,23,42,0.05)]">
            Cancel
          </button>
          <button onClick={() => submit(false)} disabled={busy !== null}
                  style={{ background: CC.ink }}
                  className="flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[12px] font-medium
                             text-white transition-transform active:scale-[0.97] disabled:opacity-50">
            {busy === 'save' && <Loader2 size={13} className="animate-spin" />}
            {existing ? 'Update' : 'Mark down'}
          </button>
        </footer>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="grid gap-1.5">
      <span className={EYEBROW}>{label}</span>
      {children}
    </label>
  )
}
